import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BsCalendar3, BsFillPeopleFill, BsArrowLeft, BsSearch } from 'react-icons/bs';
import { HiSparkles } from 'react-icons/hi';
import axios from 'axios';
import moment from 'moment-timezone';
import { Buffer } from 'buffer';
import LoadingScreen from '../component/LoadingScreen';
import UserTopbar from '../component/UserTopbar';
import { InteractionStatus } from '@azure/msal-browser';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { useMsal } from '@azure/msal-react';
import { checkIsAdmin } from '../callMsGroup';
import { getImageSrc } from '../utils/imageSrc';
import { useLang, useLabels } from '../hooks/useLabels';

type SectionKey = 'happening' | 'upcoming' | 'recently';

interface EventItem {
    id: number;
    eventNumber: string;
    eventName: string;
    description: string;
    startDate: string;
    endDate: string;
    image: string;
    owner: string;
    totalRecieved: number;
    totalMember: number;
    memberGroup: number;
    createdAt?: string;
}

function EventCard({ event, onClick, badge, badgeType }: {
    event: EventItem;
    onClick: () => void;
    badge?: string;
    badgeType?: 'live' | 'upcoming' | 'soon';
}) {
    return (
        <div className="he-card" onClick={onClick}>
            <div className="he-card-img-wrap">
                <img
                    src={getImageSrc(event.image)}
                    alt={event.eventName}
                    className="he-card-img"
                />
                {badge && (
                    <span className={`he-card-badge he-card-badge--${badgeType || 'upcoming'}`}>
                        {badgeType === 'live' && <span className="he-card-badge-dot" />}
                        {badge}
                    </span>
                )}
            </div>
            <div className="he-card-body">
                <h3 className="he-card-title">{event.eventName}</h3>
                <p className="he-card-desc">{event.description}</p>
                <div className="he-card-meta">
                    <span className="he-card-meta-item">
                        <BsCalendar3 />
                        {moment.utc(event.startDate).tz('Asia/Bangkok').format('DD MMM YYYY')}
                    </span>
                    <span className="he-card-meta-item">
                        <BsFillPeopleFill />
                        {event.totalRecieved}
                        {event.memberGroup !== 3 ? `/${event.totalMember}` : ''}
                    </span>
                </div>
            </div>
        </div>
    );
}


export default function BrowseEvents() {
    const { section } = useParams<{ section: SectionKey }>();
    const navigate = useNavigate();
    const { inProgress, instance, accounts } = useMsal();

    const [allEvents, setAllEvents] = useState<EventItem[]>([]);
    const [stateLoad, setStateLoad] = useState(false);
    const [token, setToken] = useState('');
    const [tokenProfile, setTokenProfile] = useState('');
    const [photoData, setPhotoData] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [search, setSearch] = useState('');
    const [lang, setLang] = useLang();
    const { t } = useLabels('home', token, lang);

    const SECTION_META: Record<SectionKey, { title: string; icon: React.ReactNode; color: string }> = {
        happening: {
            title: t('section.happening', 'Happening Now'),
            icon: <span className="he-live-dot" style={{ display: 'inline-block', marginRight: 6 }} />,
            color: '#e53935',
        },
        upcoming: {
            title: t('section.upcoming', 'Upcoming Events'),
            icon: <span className="he-section-icon-dot he-section-icon-dot--upcoming" style={{ display: 'inline-block', marginRight: 6 }} />,
            color: '#1976d2',
        },
        recently: {
            title: t('section.recently', 'Recently Added'),
            icon: <HiSparkles style={{ marginRight: 6, color: '#f59e0b' }} />,
            color: '#f59e0b',
        },
    };

    useEffect(() => {
        if (!token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                if (!res) return;
                setToken(res.accessToken);
                setTokenProfile(res.accessTokenProfile);
                try {
                    const r1 = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                        responseType: 'arraybuffer',
                    });
                    setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r1.data, 'binary').toString('base64'));
                } catch {}
            });
        }
    }, [inProgress]);

    useEffect(() => {
        if (!tokenProfile) return;
        checkIsAdmin(tokenProfile).then(setIsAdmin).catch(() => {});
    }, [tokenProfile]);

    useEffect(() => {
        if (!token) return;
        axios.get(process.env.REACT_APP_API_END_POINT + '/v1/Events/events', {
            headers: {
                'Content-type': 'application/json',
                Authorization: 'Bearer ' + (sessionStorage.getItem('accessToken') || token),
            },
            params: { keyword: '', limit: 500, offset: 0 },
        }).then((res) => {
            const list = (res.data.eventsLists ?? res.data ?? []).sort(
                (a: any, b: any) =>
                    new Date(b.createdAt || b.startDate).getTime() -
                    new Date(a.createdAt || a.startDate).getTime()
            );
            setAllEvents(list);
        }).catch(console.error)
          .finally(() => setStateLoad(true));
    }, [token]);

    const today = moment.utc().tz('Asia/Bangkok').startOf('day');
    const isHappeningNow = (ev: EventItem) =>
        moment.utc(ev.startDate).tz('Asia/Bangkok').startOf('day').isSameOrBefore(today) &&
        moment.utc(ev.endDate).tz('Asia/Bangkok').startOf('day').isSameOrAfter(today);
    const isUpcoming = (ev: EventItem) => moment.utc(ev.startDate).tz('Asia/Bangkok').startOf('day').isAfter(today);

    const getDaysUntil = (dateStr: string): { label: string; type: 'live' | 'upcoming' | 'soon' } => {
        const diff = moment.utc(dateStr).tz('Asia/Bangkok').startOf('day').diff(today, 'days');
        if (diff <= 3) return {
            label: diff === 0 ? t('badge.today', 'Today') : diff === 1 ? t('badge.tomorrow', 'Tomorrow') : `${diff} ${t('badge.days', 'days')}`,
            type: 'soon',
        };
        return { label: `${t('badge.in', 'In')} ${diff} ${t('badge.days', 'days')}`, type: 'upcoming' };
    };

    const sectionKey = (section as SectionKey) || 'upcoming';
    const meta = SECTION_META[sectionKey] ?? SECTION_META.upcoming;

    const getEvents = (): EventItem[] => {
        switch (sectionKey) {
            case 'happening':
                return allEvents.filter(isHappeningNow);
            case 'upcoming':
                return allEvents
                    .filter(isUpcoming)
                    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
            case 'recently':
            default:
                return allEvents;
        }
    };

    const q = search.toLowerCase();
    const baseEvents = getEvents();
    const filtered = q
        ? baseEvents.filter(
              (ev) =>
                  ev.eventName.toLowerCase().includes(q) ||
                  (ev.description ?? '').toLowerCase().includes(q)
          )
        : baseEvents;

    if (!stateLoad) return <LoadingScreen />;

    return (
        <div>
            <UserTopbar
                isAdmin={isAdmin}
                photoData={photoData}
                triggerSearch={() => {}}
            />
            <main style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

                {/* Back + heading */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: 'var(--n-100)', border: 'none',
                            borderRadius: 'var(--r-sm)', padding: '7px 14px',
                            fontSize: '0.875rem', fontWeight: 500,
                            color: 'var(--n-700)', cursor: 'pointer',
                        }}
                    >
                        <BsArrowLeft size={15} /> {t('btn.back', 'Back')}
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {meta.icon}
                        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: 'var(--n-900)' }}>
                            {meta.title}
                        </h1>
                        <span style={{
                            background: 'var(--n-100)', color: 'var(--n-500)',
                            borderRadius: 999, padding: '2px 10px',
                            fontSize: '0.8rem', fontWeight: 600,
                        }}>
                            {filtered.length}
                        </span>
                    </div>
                </div>

                {/* Search */}
                <div style={{ position: 'relative', maxWidth: 420, marginBottom: 28 }}>
                    <BsSearch style={{
                        position: 'absolute', left: 12, top: '50%',
                        transform: 'translateY(-50%)', color: 'var(--n-400)',
                    }} />
                    <input
                        type="text"
                        placeholder={t('browse.search_placeholder', 'Search events…')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: '100%', padding: '9px 12px 9px 36px',
                            border: '1px solid var(--n-200)', borderRadius: 'var(--r-sm)',
                            fontSize: '0.9rem', outline: 'none', background: '#fff',
                        }}
                    />
                </div>

                {/* Grid */}
                {filtered.length > 0 ? (
                    <div className="he-grid">
                        {filtered.map((ev) => {
                            if (sectionKey === 'happening') {
                                return (
                                    <EventCard
                                        key={ev.eventNumber}
                                        event={ev}
                                        onClick={() => navigate(`/event/${ev.eventNumber}`)}
                                        badge={t('badge.live', 'LIVE')}
                                        badgeType="live"
                                    />
                                );
                            }
                            if (sectionKey === 'upcoming') {
                                const { label, type } = getDaysUntil(ev.startDate);
                                return (
                                    <EventCard
                                        key={ev.eventNumber}
                                        event={ev}
                                        onClick={() => navigate(`/event/${ev.eventNumber}`)}
                                        badge={label}
                                        badgeType={type}
                                    />
                                );
                            }
                            return (
                                <EventCard
                                    key={ev.eventNumber}
                                    event={ev}
                                    onClick={() => navigate(`/event/${ev.eventNumber}`)}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--n-400)' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔍</div>
                        <p style={{ fontSize: '1rem', fontWeight: 500 }}>{t('browse.no_events', 'No events found')}</p>
                    </div>
                )}
            </main>
        </div>
    );
}
